/*!
 * cw-cocktail-hub.js
 * Client-side logic for /pages/cocktails:
 *   - Filter rail + grid + URL hash state (Section 5.1 / 5.2)
 *   - Recipe drawer (Section 5.3)
 *   - Menu Builder: tick cocktails → floating panel → modal with GP maths
 *     + bulk cart add via /cart/add.js (Section 5.4)
 *
 * Reads JSON from #cw-cocktail-data (rendered by cw-cocktail-data-island snippet).
 * Persists picked cocktails in localStorage under CW_CH_MENU_KEY.
 *
 * Vanilla JS, no dependencies. Deliberately monolithic — one page, one file.
 * Spec: docs/cocktails-hub/DESIGN.md §5
 */
(function () {
  'use strict';

  // ==========================================================================
  // 1. Data load + boot guard
  // ==========================================================================
  var dataEl = document.getElementById('cw-cocktail-data');
  if (!dataEl) return;

  var ALL = [];
  try { ALL = JSON.parse((dataEl.textContent || '[]').trim() || '[]'); }
  catch (e) { console.error('[cw-cocktail-hub] data parse failed', e); return; }

  // Defensive normalization: Liquid's `json` filter on metaobject multi-line
  // text fields can emit `{"error":"json not allowed for this object"}` if the
  // field wasn't passed through `capture` first. The server-side fix is in
  // snippets/cw-cocktail-data-island.liquid but until edge caches catch up,
  // any non-string n/o/i/m would crash countIngredients/basesOf/cardHTML
  // (calls like `s.split` throw on objects). Coerce to empty string.
  for (var ni = 0; ni < ALL.length; ni++) {
    var nc = ALL[ni];
    if (typeof nc.n !== 'string') nc.n = nc.h || '';
    if (typeof nc.o !== 'string') nc.o = '';
    if (typeof nc.i !== 'string') nc.i = '';
    if (typeof nc.m !== 'string') nc.m = '';
    if (typeof nc.alt !== 'string') nc.alt = nc.n || '';
    if (typeof nc.p !== 'string' && nc.p !== null) nc.p = null;
  }

  // Index by handle for O(1) lookup.
  var BY_HANDLE = Object.create(null);
  for (var i = 0; i < ALL.length; i++) BY_HANDLE[ALL[i].h] = ALL[i];

  var root       = document.querySelector('[data-cw-hub-discovery]');
  var grid       = root && root.querySelector('[data-cw-hub-grid]');
  var filterRail = root && root.querySelector('[data-cw-hub-filters]');
  var countEl    = root && root.querySelector('[data-cw-hub-count]');
  var sortEl     = root && root.querySelector('[data-cw-hub-sort]');
  var drawer     = document.querySelector('[data-cw-hub-drawer]');
  var builder    = document.querySelector('[data-cw-builder]');

  // ==========================================================================
  // 2. State
  // ==========================================================================
  var STATE = {
    filters: { base: new Set(), prod: new Set(), liq: new Set(), ing: new Set() },
    sort: 'featured',
    menu: new Set(),  // cocktail handles in the menu builder
    pours: new Set()  // product handles the user already stocks (reverse lookup)
  };

  var CW_CH_MENU_KEY = 'cw-ch-menu-v1';
  var CW_CH_POURS_KEY = 'cw-ch-pours-v1';

  function loadMenuFromStorage() {
    try {
      var raw = window.localStorage.getItem(CW_CH_MENU_KEY);
      if (!raw) return;
      var arr = JSON.parse(raw);
      if (Array.isArray(arr)) arr.forEach(function (h) { if (BY_HANDLE[h]) STATE.menu.add(h); });
    } catch (e) { /* ignore */ }
  }
  function saveMenuToStorage() {
    try {
      window.localStorage.setItem(CW_CH_MENU_KEY, JSON.stringify(Array.from(STATE.menu)));
    } catch (e) { /* quota or disabled — ignore */ }
  }
  function loadPoursFromStorage() {
    try {
      var raw = window.localStorage.getItem(CW_CH_POURS_KEY);
      if (!raw) return;
      var arr = JSON.parse(raw);
      if (Array.isArray(arr)) arr.forEach(function (h) { STATE.pours.add(h); });
    } catch (e) { /* ignore */ }
  }
  function savePoursToStorage() {
    try {
      window.localStorage.setItem(CW_CH_POURS_KEY, JSON.stringify(Array.from(STATE.pours)));
    } catch (e) { /* ignore */ }
  }

  // BOTTLE_INDEX: handle → { handle, title, vendor, type, vid, px, cocktailHandles: [] }
  // Built once after the data island normalisation. Used by the reverse-lookup
  // module to render the bottle picker, compute completeness, and rank lift.
  var BOTTLE_INDEX = (function () {
    var idx = Object.create(null);
    for (var i = 0; i < ALL.length; i++) {
      var c = ALL[i];
      for (var j = 0; j < c.u.length; j++) {
        var p = c.u[j];
        if (!p || !p.h) continue;
        if (p.v === 'Massenez') continue; // decoy policy — never surface in picker
        var e = idx[p.h];
        if (!e) {
          e = idx[p.h] = {
            h: p.h, t: p.t || p.h, v: p.v || '', ty: p.ty || '',
            vid: p.vid || 0, px: p.px || 0,
            cocktailHandles: []
          };
        }
        e.cocktailHandles.push(c.h);
      }
    }
    return idx;
  })();

  // CATEGORY_INDEX + SPIRITS_BY_HANDLE: parsed from #cw-spirits-data island
  // (rendered by snippets/cw-spirits-data-island.liquid). The full Bouchon
  // spirits catalogue bucketed by category-collection — used to show "Browse
  // our Gin range" with the top-3 SKU options on every category-matched
  // ingredient line in the drawer.
  var CATEGORY_INDEX = Object.create(null);   // 'gin' → {label, url, handles[]}
  var SPIRITS_BY_HANDLE = Object.create(null);// 'cer-bluecoat-700' → {t, v, px, vid}
  (function loadSpiritsData() {
    var spiritsEl = document.getElementById('cw-spirits-data');
    if (!spiritsEl) return;
    var data;
    try { data = JSON.parse((spiritsEl.textContent || '{}').trim() || '{}'); }
    catch (e) { console.error('[cw-cocktail-hub] spirits data parse failed', e); return; }
    if (data.byCategory) {
      Object.keys(data.byCategory).forEach(function (k) {
        var c = data.byCategory[k];
        if (!c) return;
        CATEGORY_INDEX[k] = {
          label: typeof c.label === 'string' ? c.label : k,
          url: typeof c.url === 'string' ? c.url : ('/collections/' + k),
          handles: Array.isArray(c.handles) ? c.handles : []
        };
      });
    }
    if (data.byHandle) {
      Object.keys(data.byHandle).forEach(function (h) {
        var b = data.byHandle[h];
        if (!b) return;
        SPIRITS_BY_HANDLE[h] = {
          h: h,
          t: typeof b.t === 'string' ? b.t : h,
          v: typeof b.v === 'string' ? b.v : '',
          px: typeof b.px === 'number' ? b.px : 0,
          vid: typeof b.vid === 'number' ? b.vid : 0
        };
      });
    }
  })();

  // Given a COLLECTION_HINTS URL (e.g. "/collections/gin"), return the
  // corresponding category key. Used to bridge regex matches → CATEGORY_INDEX.
  function categoryKeyFromUrl(url) {
    if (!url) return null;
    var m = url.match(/\/collections\/([a-z0-9-]+)/i);
    return m ? m[1] : null;
  }

  // ==========================================================================
  // 3. Helpers
  // ==========================================================================
  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHTML(s); }
  function fmt2(n) { return (Math.round((+n || 0) * 100) / 100).toFixed(2); }

  function countIngredients(s) {
    return (s || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean).length;
  }

  function inferBase(typeLower, tagsJoined) {
    var lookups = [
      ['cognac', 'Cognac'], ['armagnac', 'Armagnac'], ['calvados', 'Calvados'],
      ['bourbon', 'Bourbon'], ['whisky', 'Whisky'], ['whiskey', 'Whisky'], ['rye', 'Rye'],
      ['rum', 'Rum'], ['cachaca', 'Cachaça'],
      ['gin', 'Gin'], ['tequila', 'Tequila'], ['mezcal', 'Mezcal'],
      ['vermouth', 'Vermouth'], ['amaro', 'Amaro'], ['bitter', 'Bitter'],
      ['absinthe', 'Absinthe'], ['absynthe', 'Absinthe'], ['pastis', 'Pastis'],
      ['vodka', 'Vodka'],
      ['eau de vie', 'Eau de Vie'], ['fruit spirit', 'Eau de Vie'], ['fruit in spirit', 'Eau de Vie'],
      ['fruit liqueur', 'Liqueur'], ['fruit cremes', 'Liqueur'],
      ['italian liqueur', 'Liqueur'], ['other liqueur', 'Liqueur'],
      ['lemoncello', 'Liqueur'], ['sambuca', 'Liqueur'], ['schnapps', 'Liqueur'],
      ['liqueur', 'Liqueur'],
      ['pisco', 'Pisco'], ['grappa', 'Grappa'], ['marc', 'Marc'], ['brandy', 'Brandy'],
      ['aperitif', 'Aperitif'], ['aperitvo', 'Aperitif'], ['aperitivo', 'Aperitif']
    ];
    for (var i = 0; i < lookups.length; i++) if (typeLower.indexOf(lookups[i][0]) !== -1) return lookups[i][1];
    if (tagsJoined.indexOf('spirit:eau-de-vie') !== -1) return 'Eau de Vie';
    if (tagsJoined.indexOf('spirit:armagnac') !== -1)   return 'Armagnac';
    if (tagsJoined.indexOf('spirit:absinthe') !== -1)   return 'Absinthe';
    if (tagsJoined.indexOf('spirit:liqueur') !== -1)    return 'Liqueur';
    return '';
  }

  // Base spirit inference scans BOTH products_used (for liqueurs/aperitifs we
  // distribute) AND the cocktail's ingredient text (for commodity base spirits
  // like gin, vodka, scotch that aren't in our SKU range for every cocktail).
  // Without the ingredient-text fallback, recipes like Aviation (uses generic
  // gin + our Vedrenne Maraschino + Violette) classify as "Liqueur" only and
  // never match the Gin filter. Cocktails like Espresso Martini, Negroni,
  // Margarita, Daiquiri, etc. all rely on this fallback.
  var INGREDIENT_BASES = [
    [/\b(gin)\b/i, 'Gin'],
    [/\b(vodka)\b/i, 'Vodka'],
    [/\b(white rum|aged rum|dark rum|spiced rum|rhum agricole|rhum|rum)\b/i, 'Rum'],
    [/\b(tequila)\b/i, 'Tequila'],
    [/\b(mezcal|mescal)\b/i, 'Mezcal'],
    [/\b(bourbon)\b/i, 'Bourbon'],
    [/\b(rye whiskey|rye)\b/i, 'Rye'],
    [/\b(scotch|whisky|whiskey)\b/i, 'Whisky'],
    [/\b(cognac)\b/i, 'Cognac'],
    [/\b(armagnac)\b/i, 'Armagnac'],
    [/\b(calvados|apple brandy)\b/i, 'Calvados'],
    [/\b(brandy)\b/i, 'Brandy'],
    [/\b(cacha[çc]a)\b/i, 'Cachaça'],
    [/\b(pisco)\b/i, 'Pisco'],
    [/\b(sweet vermouth|dry vermouth|vermouth)\b/i, 'Vermouth'],
    [/\b(amaro)\b/i, 'Amaro'],
    [/\b(absinthe|absynthe)\b/i, 'Absinthe'],
    [/\b(pastis)\b/i, 'Pastis'],
    [/\b(champagne|cr[eé]mant|prosecco|sparkling)\b/i, 'Champagne'],
    [/\b(sake)\b/i, 'Sake']
  ];

  function basesOf(c) {
    var seen = Object.create(null), out = [];

    // 1. From products_used (current behavior — picks up liqueurs / vermouth / etc.)
    for (var i = 0; i < c.u.length; i++) {
      var p = c.u[i];
      var label = inferBase((p.ty || '').toLowerCase(), (p.tg || '').toLowerCase());
      if (label && !seen[label]) { seen[label] = 1; out.push(label); }
    }

    // 2. From ingredients text (word-boundary regex, case-insensitive).
    //    Only checks for spirits NOT already found in products_used.
    var ing = c.i || '';
    if (ing) {
      for (var k = 0; k < INGREDIENT_BASES.length; k++) {
        var label2 = INGREDIENT_BASES[k][1];
        if (seen[label2]) continue;
        if (INGREDIENT_BASES[k][0].test(ing)) { seen[label2] = 1; out.push(label2); }
      }
    }
    return out;
  }

  // ==========================================================================
  // Recipe ↔ Bouchon SKU mapping
  // ==========================================================================
  // Maps an ingredient category (detected from recipe text) to:
  //   - label / url (the "Browse our X range →" link)
  //   - pinned (optional array of product handles — Chris's editorial picks
  //             that show first as "Our pick" cards)
  //
  // When a recipe line is detected as a category:
  //   1. Pinned handles render first (1-2 of them, badged "Our pick")
  //   2. Remaining slots (up to 4 total) fill from CATEGORY_INDEX.handles
  //   3. "View all N options →" link to the collection page
  //
  // Match order matters — more-specific patterns first.
  // Editorial pins per Chris 2026-05-22.
  var COLLECTION_HINTS = [
    // Liqueurs (most-specific first)
    { re: /cr[èe]me de cassis|cassis liqueur/i, label: 'Crème de Cassis', url: '/collections/liqueurs',
      pinned: ['gabriel-boudier-creme-de-cassis-20-700ml', 'cer-vesupcas700'] },                 // Boudier Cassis + Vedrenne Supercassis
    { re: /maraschino/i,                        label: 'Maraschino',      url: '/collections/liqueurs',
      pinned: ['luxardo-maraschino-liqueur', 'cer-vemar700'] },                                   // Luxardo Maraschino (canonical) + Vedrenne Maraschino
    { re: /cr[èe]me de violette|violet liqueur/i,label: 'Crème de Violette', url: '/collections/liqueurs',
      pinned: ['cer-vevio700ml', 'gabriel-boudier-violet-18-700ml'] },                            // Vedrenne Violette + Boudier Violet
    { re: /elderflower/i,                       label: 'Elderflower Liqueur', url: '/collections/liqueurs',
      pinned: ['gabriel-boudier-elderflower-liqueur-18-700ml', 'gabriel-boudier-suro-elderflower-20-700ml'] }, // Boudier Elderflower + Suro
    { re: /cr[èe]me de m[ûu]re|blackberry liqueur/i, label: 'Crème de Mûre',  url: '/collections/liqueurs',
      pinned: ['vedrenne-creme-de-mure-blackberry-liqueur', 'gabriel-boudier-mure-liqueur-20-700ml'] }, // Vedrenne + Boudier Mûre
    { re: /cr[èe]me de p[êe]che|peach liqueur/i,label: 'Peach Liqueur',   url: '/collections/liqueurs',
      pinned: ['vedrenne-creme-de-peche-de-vigne-vineyard-peach-liqueur', 'gabriel-boudier-vineyard-peach-liqueur-18-700ml'] }, // Vedrenne + Boudier Vineyard Peach
    { re: /cr[èe]me de cacao|cacao liqueur/i,   label: 'Crème de Cacao',  url: '/collections/liqueurs',
      pinned: ['cer-boudiercacw700ml', 'cer-boudiercacnoir500ml'] },                              // Boudier Cacao Blanc + Noir
    { re: /cr[èe]me de menthe|menthe liqueur/i, label: 'Crème de Menthe', url: '/collections/liqueurs',
      pinned: ['cer-boudiermentheverte700ml', 'cer-vemenver700'] },                               // Boudier Menthe Verte + Vedrenne Green Mint
    { re: /cr[èe]me de framboise|raspberry liqueur/i, label: 'Crème de Framboise', url: '/collections/liqueurs',
      pinned: ['vedrenne-creme-de-framboise-raspberry-liqueur', 'gabriel-boudier-raspberry-liqueur-20-700ml'] }, // Vedrenne + Boudier Raspberry
    { re: /cr[èe]me de fraise|strawberry liqueur/i,   label: 'Crème de Fraise',    url: '/collections/liqueurs',
      pinned: ['gabriel-boudier-strawberry-liqueur-20-700ml'] },                                  // Boudier Strawberry
    { re: /apricot brandy|apricot liqueur/i,    label: 'Apricot Liqueur', url: '/collections/liqueurs',
      pinned: ['cer-boudierapricotbrandy700ml', 'gabriel-boudier-apricot-brandy-liqueur-24-500ml'] }, // Boudier Apricot Brandy 700ml + 500ml
    { re: /triple sec|cointreau|grand marnier|orange liqueur|curaçao|curacao/i, label: 'Triple Sec / Orange Liqueur', url: '/collections/liqueurs',
      pinned: ['vedrenne-liqueur-triple-sec-white-curacao', 'cer-boudierts700ml', 'gabriel-boudier-triple-sec-38-500ml', 'cer-boudiercblue700ml'] },
    { re: /amaretto/i,                          label: 'Amaretto',        url: '/collections/liqueurs',
      pinned: ['a-beramar'] },                                                                     // Berta Amaretto di Mombaruzzo
    { re: /limoncello|lemoncello/i,             label: 'Limoncello',      url: '/collections/liqueurs',
      pinned: ['three-foxes-limoncello-20-700ml'] },                // Three Foxes Limoncello
    { re: /chartreuse/i,                        label: 'Chartreuse alternative', url: '/collections/liqueurs',
      pinned: ['dolin-liqueur-de-genepi-chamois-45-750ml', 'cer-boudierfontbonne', 'cer-vesalersgent'] },  // Dolin Chamois Génépi + Boudier Fontbonne + Salers Gentian (auto-detected on 31 cocktails)
    { re: /\b(gentian|genti?ane)\b/i,           label: 'Gentian Aperitif', url: '/collections/aperitifs',
      pinned: ['cer-vesalersgent', 'cer-ddpgentiane'] },              // Salers + DDP Gentiane de Lure
    { re: /b[ée]n[ée]dictine/i,                 label: 'Bénédictine',     url: '/collections/liqueurs' },
    { re: /pastis/i,                            label: 'Pastis',          url: '/collections/pastis',
      pinned: ['cer-ddphbpasbox-bottle'] },                          // Henri Bardouin Pastis
    { re: /absinthe|absynthe/i,                 label: 'Absinthe',        url: '/collections/absinthe',
      pinned: ['cer-ddpabsent','cer-ddpgdeabsent'] },  // Absente 55 + Grande Absente 69

    // Vermouth + aperitifs — Chris: "always prioritize Dolin" (+ Three Foxes Ruby as secondary for sweet)
    { re: /sweet vermouth|red vermouth|vermouth ross[oa]|vermouth rouge/i, label: 'Sweet Vermouth', url: '/collections/vermouth',
      pinned: ['cer-dolrouge', 'three-foxes-ruby-vermouth-17-700ml'] },  // Dolin Rouge primary + Three Foxes Ruby secondary
    { re: /dry vermouth|vermouth dry/i,        label: 'Dry Vermouth',  url: '/collections/vermouth',
      pinned: ['cer-doldry'] },                // Dolin Dry
    { re: /vermouth blanc|white vermouth|bianco vermouth/i, label: 'Blanc Vermouth', url: '/collections/vermouth',
      pinned: ['cer-dolblanc'] },              // Dolin Blanc
    { re: /vermouth/i,                          label: 'Vermouth',        url: '/collections/vermouth',
      pinned: ['cer-dolrouge', 'three-foxes-ruby-vermouth-17-700ml'] },  // Generic vermouth: Dolin Rouge + Three Foxes Ruby
    { re: /amaro/i,                             label: 'Amaro',           url: '/collections/spirits',
      pinned: ['three-foxes-org-espresso-amaro'] },                  // Three Foxes Organic Espresso Amaro
    { re: /campari|italian bitter/i,            label: 'Bitter Aperitif (Campari alternative)', url: '/collections/aperitifs',
      pinned: ['cer-dolbitter', 'three-foxes-triple-bitter-27-700ml'] },  // Dolin Bitter + Three Foxes Triple Bitter
    { re: /aperol/i,                            label: 'Italian Aperitif (Aperol alternative)', url: '/collections/aperitifs',
      pinned: ['three-foxes-aperitivo-sunny-arvo-750ml'] },        // Three Foxes Sunny Arvo
    { re: /lillet/i,                            label: 'Aperitif Wine',   url: '/collections/aperitifs',
      pinned: ['cer-vemaurin1000ml'] },                                                            // Maurin Quina Le Puy (Lillet-adjacent aperitif wine; closest Caravan stocks)
    { re: /aperitif|ap[ée]ritif/i,              label: 'Aperitif',        url: '/collections/aperitifs' },

    // Base spirits — editorial pins per Chris
    { re: /\b(gin)\b/i,                         label: 'Gin',             url: '/collections/gin',
      pinned: ['nosferatu-dead-dry-gin-700ml', 'three-foxes-organic-rosella-gin-40-700ml'] },  // Nosferatu Dead Dry primary + Three Foxes Rosella secondary
    { re: /\b(vodka)\b/i,                       label: 'Vodka',           url: '/collections/vodka',
      pinned: ['hartshorn-pure-sheep-whey-vodka-500ml'] },         // Hartshorn Pure Vodka
    { re: /aged rum|dark rum|spiced rum/i,      label: 'Aged / Dark Rum', url: '/collections/rum',
      pinned: ['cer-boderum2maderasatlantic'] },                   // Dos Maderas 5+1
    { re: /white rum|light rum|rhum agricole|rhum blanc/i, label: 'White Rum / Agricole', url: '/collections/rum',
      pinned: ['jm-jardin-macouba-white-rum-ag'] },                // Rhum JM Blanc (Jardin Macouba)
    { re: /\b(rhum|rum)\b/i,                    label: 'Rum',             url: '/collections/rum',
      pinned: ['cer-boderum2maderasatlantic','jm-jardin-macouba-white-rum-ag'] }, // both — dark + white
    { re: /tequila reposado|reposado/i,         label: 'Tequila Reposado',url: '/collections/tequila',
      pinned: ['don-fulano-reposado-tequila-40-700ml'] },                                          // Don Fulano Reposado
    { re: /tequila blanco|blanco tequila/i,     label: 'Tequila Blanco',  url: '/collections/tequila',
      pinned: ['don-fulano-blanco-tequila-40-700ml'] },            // Cimarron not in catalog yet — Don Fulano Blanco temp
    { re: /\b(tequila)\b/i,                     label: 'Tequila',         url: '/collections/tequila',
      pinned: ['don-fulano-blanco-tequila-40-700ml'] },
    { re: /\b(mezcal|mescal)\b/i,               label: 'Mezcal',          url: '/collections/mezcal',
      pinned: ['cer-dommezcalclasico700ml'] },                     // Domingo Espadin Clasico
    { re: /\b(bourbon)\b/i,                     label: 'Bourbon',         url: '/collections/whisky' },
    { re: /\b(rye)\s*(whiskey|whisky)?/i,       label: 'Rye Whiskey',     url: '/collections/whisky',
      pinned: ['sortilege-rye-whisky-3-years-40-750ml'] },            // Sortilege Rye 3yr
    { re: /\b(scotch)\b/i,                      label: 'Scotch Whisky',   url: '/collections/whisky',
      pinned: ['dalmore-15-year-old-single-malt-scotch-whisky', 'cer-dumwhisky008'] },             // Dalmore 15 + Dumangin Glenrothes 2007
    { re: /\b(whisky|whiskey)\b/i,              label: 'Whisky',          url: '/collections/whisky',
      pinned: ['sortilege-rye-whisky-3-years-40-750ml'] },            // Default whisky pick = Sortilege Rye
    { re: /\b(cognac)\b/i,                      label: 'Cognac',          url: '/collections/cognac',
      pinned: ['cer-fvvs','cer-meryorgvs'] },                      // Francois Voyer VS + Mery Melrose VS
    { re: /\b(armagnac)\b/i,                    label: 'Armagnac',        url: '/collections/armagnac',
      pinned: ['cer-delovsop'] },                                  // Delord VSOP
    { re: /\b(calvados|apple brandy)\b/i,       label: 'Calvados',        url: '/collections/calvados',
      pinned: ['cer-laurdrouinselection350ml'] },                  // Drouin Selection
    { re: /\b(brandy)\b/i,                      label: 'Brandy',          url: '/collections/brandies' },
    { re: /\b(cacha[çc]a)\b/i,                  label: 'Cachaça',         url: '/collections/cachaca',
      pinned: ['cer-gersoulparent-gersoul'] },                     // Germana Soul
    { re: /\b(pisco)\b/i,                       label: 'Pisco',           url: '/collections/pisco',
      pinned: ['cer-macchupisco700ml'] },                          // Macchu Pisco
    { re: /\b(grappa)\b/i,                      label: 'Grappa',          url: '/collections/marcs-grappa' },
    { re: /\b(marc)\b/i,                        label: 'Marc',            url: '/collections/marcs-grappa' },
    { re: /eau de vie|fruit brandy/i,           label: 'Eau de Vie',      url: '/collections/eau-de-vie-fruit-brandy' },

    // Sparkling / wine
    { re: /champagne/i,                         label: 'Champagne',       url: '/collections/champagne' },
    { re: /cr[ée]mant|prosecco|cava|sparkling wine/i, label: 'Sparkling Wine', url: '/collections/sparkling' },

    // Generic last-resort
    { re: /\b(liqueur|cordial|cr[èe]me de)\b/i, label: 'Liqueur',         url: '/collections/liqueurs' },
    { re: /\b(spirit)\b/i,                      label: 'Spirit',          url: '/collections/spirits' }
  ];

  // Match an ingredient line to a Bouchon SKU by DISTINCTIVE title keyword,
  // not by vendor name. Returns the product if a non-generic, non-vendor word
  // from the product title appears in the ingredient line.
  //
  // Fixed 2026-05-22: a previous version matched on the vendor name too, so
  // every "Vedrenne ..." line resolved to whichever Vedrenne product appeared
  // first in products_used (typically Framboise) — three different ingredient
  // lines all paired to the same product. Now the vendor name is added to the
  // skip-set BEFORE matching keywords.
  function matchProductToLine(line, products) {
    if (!line || !products || !products.length) return null;
    var ll = line.toLowerCase();

    // Generic words that aren't distinguishing enough to anchor a match.
    var GENERIC_SKIP = [
      'liqueur','liqueurs','brandy','spirit','spirits','crème','creme',
      'original','pure','classic','reserve','authentic','french','italian',
      'royal','blue','red','dry','bouchon','traditional','premium','rouge',
      'blanc','white','aged','grand','grande','distillery','distill',
      'bottle','range','signature','single','double','vintage','organic'
    ];

    // Sort by descending title length so multi-word matches win over short ones.
    var ordered = products.slice().sort(function (a, b) { return (b.t || '').length - (a.t || '').length; });

    for (var i = 0; i < ordered.length; i++) {
      var p = ordered[i];
      if (!p || !p.t) continue;

      // Build the per-product skip set: GENERIC_SKIP ∪ vendor words.
      var skip = Object.create(null);
      for (var s = 0; s < GENERIC_SKIP.length; s++) skip[GENERIC_SKIP[s]] = 1;
      if (p.v) {
        p.v.toLowerCase().split(/[\s\-,()'’]+/).forEach(function (w) {
          if (w && w.length >= 3) skip[w] = 1;
        });
      }

      // Distinguishing words from the title — flavour/style/varietal keywords.
      var words = p.t.split(/[\s\-,()'’.]+/).filter(function (w) {
        if (!w) return false;
        if (/^\d/.test(w)) return false;     // skip 25%, 700ml, etc.
        if (w.length < 4) return false;      // skip short words
        var lw = w.toLowerCase();
        if (skip[lw]) return false;
        return true;
      });

      // Need at least ONE distinguishing word to match on.
      if (!words.length) continue;

      for (var j = 0; j < words.length; j++) {
        var wlc = words[j].toLowerCase();
        // Word-boundary check to avoid false positives like "ginger" matching
        // "gin" or "cane" matching "calvados" via substring contains.
        var wordRe = new RegExp('\\b' + wlc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'i');
        if (wordRe.test(ll)) return p;
      }
    }
    return null;
  }

  // Resolve an ingredient line to either {matchedProduct} (specific Bouchon SKU)
  // or {categoryHint} (link to a relevant collection + pinned picks). Returns
  // null for plain pantry items like lemon juice, sugar, ice.
  function resolveIngredientLine(line, productsUsed) {
    if (!line) return null;
    var matched = matchProductToLine(line, productsUsed);
    if (matched) return { kind: 'product', product: matched };
    for (var i = 0; i < COLLECTION_HINTS.length; i++) {
      if (COLLECTION_HINTS[i].re.test(line)) {
        return {
          kind: 'category',
          label: COLLECTION_HINTS[i].label,
          url: COLLECTION_HINTS[i].url,
          pinned: COLLECTION_HINTS[i].pinned || []
        };
      }
    }
    return null; // pantry / non-Bouchon (lemon, sugar, ice, garnish)
  }

  function liqsOf(c) {
    var seen = Object.create(null), out = [];
    for (var i = 0; i < c.u.length; i++) {
      var tg = (c.u[i].tg || '').toLowerCase();
      var m = tg.match(/flavour:[a-z0-9-]+/g);
      if (!m) continue;
      for (var j = 0; j < m.length; j++) {
        var key = m[j].replace('flavour:', '');
        if (!seen[key]) { seen[key] = 1; out.push(key); }
      }
    }
    return out;
  }

  function vendorsOf(c) {
    var seen = Object.create(null), out = [];
    for (var i = 0; i < c.u.length; i++) {
      var v = c.u[i].v;
      if (v && !seen[v]) { seen[v] = 1; out.push(v); }
    }
    return out;
  }

  function ingredientBucket(c) {
    var n = countIngredients(c.i);
    return n <= 3 ? '0-3' : (n <= 5 ? '4-5' : '6+');
  }

  // ==========================================================================
  // 4. Filter logic
  // ==========================================================================
  function matches(c) {
    if (STATE.filters.base.size) {
      var cBases = basesOf(c).map(function (b) { return b.toLowerCase(); });
      var want = Array.from(STATE.filters.base).map(function (b) { return b.toLowerCase(); });
      var ok = false;
      for (var i = 0; i < want.length; i++) if (cBases.indexOf(want[i]) !== -1) { ok = true; break; }
      if (!ok) return false;
    }
    if (STATE.filters.prod.size) {
      var cVendors = vendorsOf(c);
      var wantP = Array.from(STATE.filters.prod);
      var okP = false;
      for (var k = 0; k < wantP.length; k++) if (cVendors.indexOf(wantP[k]) !== -1) { okP = true; break; }
      if (!okP) return false;
    }
    if (STATE.filters.liq.size) {
      var cLiqs = liqsOf(c);
      var wantL = Array.from(STATE.filters.liq);
      var okL = false;
      for (var m = 0; m < wantL.length; m++) if (cLiqs.indexOf(wantL[m]) !== -1) { okL = true; break; }
      if (!okL) return false;
    }
    if (STATE.filters.ing.size) {
      if (!STATE.filters.ing.has(ingredientBucket(c))) return false;
    }
    return true;
  }

  function sorter(a, b) {
    if (STATE.sort === 'most-products') return b.u.length - a.u.length;
    if (STATE.sort === 'name-az')       return a.n.localeCompare(b.n);
    if (STATE.sort === 'fewest-ings')   return countIngredients(a.i) - countIngredients(b.i);
    return 0;
  }

  // ==========================================================================
  // 5. Card render
  // ==========================================================================
  function tickButtonHTML(handle, isTicked) {
    var label = isTicked ? 'Remove' : 'Save for later';
    var icon = isTicked
      ? '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><polyline points="3,7 6,10 11,4"/></svg>'
      : '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><line x1="7" y1="3" x2="7" y2="11"/><line x1="3" y1="7" x2="11" y2="7"/></svg>';
    return '<button class="cw-ch-card__tick' + (isTicked ? ' is-ticked' : '') + '" '
         + 'type="button" data-cw-tick="' + escapeAttr(handle) + '" '
         + 'aria-label="' + label + '" aria-pressed="' + (isTicked ? 'true' : 'false') + '">' + icon + '</button>';
  }

  function cardHTML(c) {
    var isTicked = STATE.menu.has(c.h);
    var bases = basesOf(c);
    var photo;
    if (c.p) {
      photo = '<img class="cw-ch-card__img" src="' + escapeAttr(c.p) + '" alt="' + escapeAttr(c.alt || c.n) + '" loading="lazy">';
    } else {
      // Per-spirit tinted fallback so missing-photo cards still look intentional.
      var primarySpirit = (bases[0] || 'cocktail').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      photo = '<div class="cw-ch-card__nophoto cw-ch-card__nophoto--spirit-' + escapeAttr(primarySpirit) + '" aria-hidden="true">'
            +   '<span class="cw-ch-card__nophoto-spirit">' + escapeHTML(bases[0] || 'Cocktail') + '</span>'
            +   '<span class="cw-ch-card__nophoto-name">' + escapeHTML(c.n) + '</span>'
            + '</div>';
    }
    var chipHTML = bases.slice(0, 3)
      .map(function (b) { return '<span class="cw-ch-card__chip">' + escapeHTML(b) + '</span>'; })
      .join('');
    var ingCount = countIngredients(c.i);
    var lvl = ingCount <= 3 ? 1 : (ingCount <= 5 ? 2 : 3);
    var dotsRow = '';
    for (var d = 0; d < 3; d++) {
      dotsRow += '<span class="cw-ch-card__dot' + (d < lvl ? ' is-on' : '') + '" aria-hidden="true"></span>';
    }
    var complexityLabel = lvl === 1 ? 'Easy' : (lvl === 2 ? 'Medium' : 'Advanced');
    return ''
      + '<li class="cw-ch-card' + (isTicked ? ' is-ticked' : '') + '" data-cw-card data-handle="' + escapeAttr(c.h) + '">'
      +   tickButtonHTML(c.h, isTicked)
      +   '<button class="cw-ch-card__btn" type="button" data-cw-open="' + escapeAttr(c.h) + '" aria-label="View recipe for ' + escapeAttr(c.n) + '">'
      +     '<div class="cw-ch-card__photo">' + photo + '</div>'
      +     '<div class="cw-ch-card__body">'
      +       '<h3 class="cw-ch-card__name">' + escapeHTML(c.n) + '</h3>'
      +       (chipHTML ? '<div class="cw-ch-card__chips">' + chipHTML + '</div>' : '')
      +       '<p class="cw-ch-card__meta">'
      +         '<span class="cw-ch-card__dots" aria-label="Complexity ' + lvl + ' of 3">' + dotsRow + '</span>'
      +         '<span>' + complexityLabel + '</span>'
      +         '<span class="cw-ch-card__sep">·</span>'
      +         '<span>' + ingCount + ' ingredient' + (ingCount === 1 ? '' : 's') + '</span>'
      +       '</p>'
      +     '</div>'
      +   '</button>'
      + '</li>';
  }

  function render() {
    if (!grid) return;
    var filtered = ALL.filter(matches);
    filtered.sort(sorter);
    grid.innerHTML = filtered.length
      ? filtered.map(cardHTML).join('')
      : '<li class="cw-ch-empty">No cocktails match these filters. <button type="button" class="cw-ch-empty__clear" data-cw-clear>Clear all</button></li>';
    if (countEl) {
      countEl.textContent = filtered.length + ' cocktail' + (filtered.length === 1 ? '' : 's');
    }
    syncURL();
    paintActiveChips();
    paintActiveFilters();
  }

  // Update tick button + card border in place WITHOUT a full grid re-render.
  function repaintCardTickStates() {
    if (!grid) return;
    var cards = grid.querySelectorAll('[data-cw-card]');
    cards.forEach(function (card) {
      var handle = card.getAttribute('data-handle');
      var isTicked = STATE.menu.has(handle);
      card.classList.toggle('is-ticked', isTicked);
      var tickBtn = card.querySelector('[data-cw-tick]');
      if (tickBtn) {
        var willReplace = tickButtonHTML(handle, isTicked);
        tickBtn.outerHTML = willReplace;
      }
    });
  }

  // ==========================================================================
  // 6. URL hash sync
  // ==========================================================================
  function syncURL() {
    var parts = [];
    if (STATE.filters.base.size) parts.push('base=' + Array.from(STATE.filters.base).map(encodeURIComponent).join(','));
    if (STATE.filters.prod.size) parts.push('producer=' + Array.from(STATE.filters.prod).map(encodeURIComponent).join(','));
    if (STATE.filters.liq.size)  parts.push('liq=' + Array.from(STATE.filters.liq).map(encodeURIComponent).join(','));
    if (STATE.filters.ing.size)  parts.push('ing=' + Array.from(STATE.filters.ing).map(encodeURIComponent).join(','));
    if (STATE.sort !== 'featured') parts.push('sort=' + STATE.sort);

    var desired = parts.length ? ('#' + parts.join('&')) : '';
    var current = window.location.hash || '';
    if (current.indexOf('cocktail=') !== -1) return;
    if (current !== desired) {
      history.replaceState(null, '', window.location.pathname + window.location.search + desired);
    }
  }

  function hydrateFromHash() {
    var h = (window.location.hash || '').replace(/^#/, '');
    if (!h) return null;
    var openHandle = null;
    h.split('&').forEach(function (part) {
      var eq = part.indexOf('=');
      if (eq === -1) return;
      var k = part.slice(0, eq);
      var v = part.slice(eq + 1);
      var vals = v.split(',').map(decodeURIComponent).filter(Boolean);
      if      (k === 'base')     vals.forEach(function (x) { STATE.filters.base.add(x); });
      else if (k === 'producer') vals.forEach(function (x) { STATE.filters.prod.add(x); });
      else if (k === 'liq')      vals.forEach(function (x) { STATE.filters.liq.add(x); });
      else if (k === 'ing')      vals.forEach(function (x) { STATE.filters.ing.add(x); });
      else if (k === 'sort')     STATE.sort = v;
      else if (k === 'cocktail') openHandle = decodeURIComponent(v);
    });
    return openHandle;
  }

  // ==========================================================================
  // 7. Active chips strip + filter rail paint
  // ==========================================================================
  function paintActiveChips() {
    if (!root) return;
    var holder = root.querySelector('[data-cw-active-chips]');
    if (!holder) return;
    var chips = [];
    ['base', 'prod', 'liq', 'ing'].forEach(function (group) {
      STATE.filters[group].forEach(function (v) {
        var label = v.charAt(0).toUpperCase() + v.slice(1);
        chips.push('<button type="button" class="cw-ch-active" data-cw-remove data-group="' + escapeAttr(group) + '" data-value="' + escapeAttr(v) + '">' + escapeHTML(label) + ' <span aria-hidden="true">×</span></button>');
      });
    });
    holder.innerHTML = chips.join('');
    holder.style.display = chips.length ? '' : 'none';
  }

  function paintActiveFilters() {
    if (!filterRail) return;
    filterRail.querySelectorAll('[data-cw-filter]').forEach(function (btn) {
      var g = btn.getAttribute('data-cw-filter');
      var v = btn.getAttribute('data-cw-value');
      var set = STATE.filters[g];
      if (set && set.has(v)) btn.classList.add('is-active'); else btn.classList.remove('is-active');
    });
  }

  // ==========================================================================
  // 8. Discovery wiring (filters, sort, clear, group toggles)
  // ==========================================================================
  // After a filter change the grid shrinks (often by 70–90%). If the user
  // was anchored partway down the (long) page, the shorter page leaves their
  // scrollY past the grid, landing them mid-reverse-lookup. Smooth-scroll the
  // discovery section's head back into view if it's slipped off-screen.
  function keepGridInView() {
    if (!root) return;
    var head = root.querySelector('.cw-ch-discovery__head');
    var anchor = head || root;
    requestAnimationFrame(function () {
      var rect = anchor.getBoundingClientRect();
      var nearTop = rect.top >= -20 && rect.top < window.innerHeight * 0.4;
      if (nearTop) return; // user is already looking at the right area
      anchor.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  function wireFilters() {
    if (!filterRail) return;
    filterRail.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cw-filter]');
      if (btn) {
        e.preventDefault();
        var g = btn.getAttribute('data-cw-filter');
        var v = btn.getAttribute('data-cw-value');
        var set = STATE.filters[g];
        if (!set) return;
        if (set.has(v)) set.delete(v); else set.add(v);
        render();
        keepGridInView();
        return;
      }
      if (e.target.closest('[data-cw-clear]')) {
        e.preventDefault();
        STATE.filters.base.clear();
        STATE.filters.prod.clear();
        STATE.filters.liq.clear();
        STATE.filters.ing.clear();
        render();
        keepGridInView();
      }
    });
    filterRail.addEventListener('click', function (e) {
      var head = e.target.closest('[data-cw-group-toggle]');
      if (!head) return;
      var group = head.closest('[data-cw-group]');
      if (group) group.classList.toggle('is-open');
    });
  }

  function wireActiveChips() {
    if (!root) return;
    var holder = root.querySelector('[data-cw-active-chips]');
    if (!holder) return;
    holder.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cw-remove]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var g = btn.getAttribute('data-group');
      var v = btn.getAttribute('data-value');
      var set = STATE.filters[g];
      if (set && set.has(v)) { set.delete(v); render(); keepGridInView(); }
    });
  }

  function wireSort() {
    if (!sortEl) return;
    sortEl.addEventListener('change', function () {
      STATE.sort = sortEl.value || 'featured';
      render();
    });
  }

  function wireGridDelegate() {
    if (!grid) return;
    grid.addEventListener('click', function (e) {
      if (e.target.closest('[data-cw-clear]')) {
        STATE.filters.base.clear();
        STATE.filters.prod.clear();
        STATE.filters.liq.clear();
        STATE.filters.ing.clear();
        render();
      }
    });
  }

  // ==========================================================================
  // 9. Drawer (recipe detail)
  // ==========================================================================
  function openDrawer(handle) {
    if (!drawer) return;
    var c = BY_HANDLE[handle];
    if (!c) return;
    var bodyEl = drawer.querySelector('[data-cw-drawer-body]');
    if (bodyEl) bodyEl.innerHTML = drawerBodyHTML(c);
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    document.body.classList.add('cw-ch-drawer-lock');
    if ((window.location.hash || '').indexOf('cocktail=') === -1) {
      history.pushState({ bxDrawer: handle }, '', window.location.pathname + window.location.search + '#cocktail=' + encodeURIComponent(handle));
    }
    var closeBtn = drawer.querySelector('[data-cw-drawer-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cw-ch-drawer-lock');
    if ((window.location.hash || '').indexOf('cocktail=') !== -1) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  function drawerBodyHTML(c) {
    // Non-Massenez first (decoy policy — see design §6.4).
    var u = c.u.slice().sort(function (a, b) {
      if (a.v === 'Massenez' && b.v !== 'Massenez') return 1;
      if (b.v === 'Massenez' && a.v !== 'Massenez') return -1;
      return 0;
    });

    var drawerBases = basesOf(c);
    var photo;
    if (c.p) {
      photo = '<img src="' + escapeAttr(c.p) + '" alt="' + escapeAttr(c.alt || c.n) + '" class="cw-ch-drawer__img">';
    } else {
      var drawerSpirit = (drawerBases[0] || 'cocktail').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      photo = '<div class="cw-ch-drawer__nophoto cw-ch-card__nophoto--spirit-' + escapeAttr(drawerSpirit) + '" aria-hidden="true">'
            +   '<span class="cw-ch-card__nophoto-spirit">' + escapeHTML(drawerBases[0] || 'Cocktail') + '</span>'
            +   '<span class="cw-ch-card__nophoto-name">' + escapeHTML(c.n) + '</span>'
            + '</div>';
    }

    var ingLines = (c.i || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);

    // Build paired ingredient rows — each row shows the recipe text plus
    // either a specific Bouchon SKU match OR a category-collection link.
    // Track which products are already paired so the next line doesn't
    // re-bind to a product that's already represented above (was the bug
    // where 3 ingredient lines all paired to the same Vedrenne Framboise).
    var usedProductHandles = Object.create(null);
    var ingRows = ingLines.map(function (line) {
      var availableProducts = u.filter(function (p) { return p && p.h && !usedProductHandles[p.h]; });
      var resolved = resolveIngredientLine(line, availableProducts);
      var lineHTML = '<div class="cw-ch-drawer__ing-line">' + escapeHTML(line) + '</div>';
      if (!resolved) {
        return '<li class="cw-ch-drawer__ing-row cw-ch-drawer__ing-row--plain">'
             +   lineHTML
             + '</li>';
      }
      if (resolved.kind === 'product') {
        var p = resolved.product;
        usedProductHandles[p.h] = 1;
        var metaParts = [];
        if (p.v) metaParts.push(escapeHTML(p.v));
        if (p.ty) metaParts.push(escapeHTML(p.ty));
        return ''
          + '<li class="cw-ch-drawer__ing-row cw-ch-drawer__ing-row--matched">'
          +   lineHTML
          +   '<a class="cw-ch-drawer__ing-link cw-ch-drawer__ing-link--product" '
          +     'href="/products/' + escapeAttr(p.h) + '" target="_blank" rel="noopener">'
          +     '<span class="cw-ch-drawer__ing-link-title">' + escapeHTML(p.t) + '</span>'
          +     '<span class="cw-ch-drawer__ing-link-meta">' + metaParts.join(' · ') + '</span>'
          +     '<span class="cw-ch-drawer__ing-link-price">$' + fmt2(p.px) + ' </span>'
          +   '</a>'
          + '</li>';
      }
      // resolved.kind === 'category'
      // Surface pinned editorial picks first ("Our pick" badge), then fill up
      // to 4 total with non-pinned bottles from CATEGORY_INDEX, then "View all".
      var catKey = categoryKeyFromUrl(resolved.url);
      var cat = catKey ? CATEGORY_INDEX[catKey] : null;
      var pinnedHandles = resolved.pinned || [];
      var pinnedBottles = pinnedHandles
        .map(function (h) {
          var b = SPIRITS_BY_HANDLE[h];
          if (b) return Object.assign({}, b, { isPinned: true });
          return null;
        })
        .filter(Boolean);

      var pinnedSet = Object.create(null);
      pinnedBottles.forEach(function (b) { pinnedSet[b.h] = 1; });

      var fillSlots = Math.max(0, 4 - pinnedBottles.length);
      var nonPinnedBottles = (cat && cat.handles)
        ? cat.handles
            .filter(function (h) { return !pinnedSet[h]; })
            .slice(0, fillSlots)
            .map(function (h) { return SPIRITS_BY_HANDLE[h]; })
            .filter(Boolean)
        : [];

      var topBottles = pinnedBottles.concat(nonPinnedBottles);
      var totalCount = cat ? cat.handles.length : 0;

      var optionsHTML = '';
      if (topBottles.length) {
        optionsHTML = '<ul class="cw-ch-drawer__cat-options" role="list">'
          + topBottles.map(function (b) {
              var pinBadge = b.isPinned
                ? '<span class="cw-ch-drawer__cat-option-pin" aria-label="Our pick">Our pick</span>'
                : '';
              return '<li class="cw-ch-drawer__cat-option' + (b.isPinned ? ' is-pinned' : '') + '">'
                +   '<a href="/products/' + escapeAttr(b.h) + '" target="_blank" rel="noopener">'
                +     pinBadge
                +     '<span class="cw-ch-drawer__cat-option-title">' + escapeHTML(b.t) + '</span>'
                +     (b.v ? '<span class="cw-ch-drawer__cat-option-meta">' + escapeHTML(b.v) + '</span>' : '')
                +     '<span class="cw-ch-drawer__cat-option-price">$' + fmt2(b.px) + ' </span>'
                +   '</a>'
                + '</li>';
            }).join('')
          + '</ul>';
      }
      var viewAllHTML = totalCount > topBottles.length
        ? '<a class="cw-ch-drawer__cat-viewall" href="' + escapeAttr(resolved.url) + '" target="_blank" rel="noopener">View all ' + totalCount + ' ' + escapeHTML(resolved.label) + ' options &rarr;</a>'
        : '<a class="cw-ch-drawer__cat-viewall" href="' + escapeAttr(resolved.url) + '" target="_blank" rel="noopener">Browse our ' + escapeHTML(resolved.label) + ' range &rarr;</a>';
      var catLabel = topBottles.length
        ? (pinnedBottles.length ? 'Our pick' + (pinnedBottles.length > 1 ? 's' : '') + ' for ' + escapeHTML(resolved.label)
                                : 'Pick from our ' + escapeHTML(resolved.label) + ' range (' + totalCount + ' bottles)')
        : 'Browse our ' + escapeHTML(resolved.label) + ' range';
      return ''
        + '<li class="cw-ch-drawer__ing-row cw-ch-drawer__ing-row--category">'
        +   lineHTML
        +   '<div class="cw-ch-drawer__cat-block">'
        +     '<p class="cw-ch-drawer__cat-eyebrow">' + catLabel + '</p>'
        +     optionsHTML
        +     viewAllHTML
        +   '</div>'
        + '</li>';
    }).join('');

    // Any products_used SKUs NOT matched inline above get listed below as
    // "additional Bouchon bottles for this serve" — covers the case where
    // a recipe line phrasing doesn't keyword-match (e.g., "Triple Sec" line
    // with a "Vedrenne Pro Orange Curaçao" product).
    var extraProducts = u
      .filter(function (p) { return !usedProductHandles[p.h]; })
      .map(function (p) {
        var meta = [];
        if (p.v) meta.push(escapeHTML(p.v));
        if (p.ty) meta.push(escapeHTML(p.ty));
        return ''
          + '<li class="cw-ch-drawer__prod">'
          +   '<a href="/products/' + escapeAttr(p.h) + '" class="cw-ch-drawer__prodlink" target="_blank" rel="noopener">'
          +     '<span class="cw-ch-drawer__prodtitle">' + escapeHTML(p.t) + '</span>'
          +     '<span class="cw-ch-drawer__prodmeta">' + meta.join(' · ') + '</span>'
          +     '<span class="cw-ch-drawer__prodprice">$' + fmt2(p.px) + ' </span>'
          +   '</a>'
          + '</li>';
      }).join('');

    var methodHTML = c.m
      ? '<h3 class="cw-ch-drawer__label">Method</h3><p class="cw-ch-drawer__method">' + escapeHTML(c.m).replace(/\n/g, '<br>') + '</p>'
      : '';

    var originHTML = c.o
      ? '<p class="cw-ch-drawer__eyebrow">' + escapeHTML(c.o) + '</p>'
      : '<p class="cw-ch-drawer__eyebrow">Cocktail</p>';

    var isTicked = STATE.menu.has(c.h);
    var addToBtnLabel = isTicked ? 'Saved — remove' : 'Save for later';

    return ''
      + '<div class="cw-ch-drawer__hero">' + photo + '</div>'
      + '<div class="cw-ch-drawer__head">'
      +   originHTML
      +   '<h2 class="cw-ch-drawer__name">' + escapeHTML(c.n) + '</h2>'
      +   '<button class="cw-ch-drawer__addto' + (isTicked ? ' is-ticked' : '') + '" type="button" data-cw-tick="' + escapeAttr(c.h) + '">'
      +     escapeHTML(addToBtnLabel)
      +   '</button>'
      + '</div>'
      + '<div class="cw-ch-drawer__recipe-wide">'
      +   '<h3 class="cw-ch-drawer__label">Build</h3>'
      +   (ingRows ? '<ul class="cw-ch-drawer__ing-rows">' + ingRows + '</ul>' : '')
      +   methodHTML
      +   (extraProducts
          ? '<h3 class="cw-ch-drawer__label cw-ch-drawer__label--mt">Other bottles we ship for this serve</h3>'
            + '<ul class="cw-ch-drawer__prodlist">' + extraProducts + '</ul>'
          : '')
      + '</div>';
  }

  function wireDrawer() {
    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-cw-open]');
      if (openBtn) {
        // Ignore clicks that originated on the tick button.
        if (e.target.closest('[data-cw-tick]')) return;
        e.preventDefault();
        openDrawer(openBtn.getAttribute('data-cw-open'));
        return;
      }
      if (e.target.closest('[data-cw-drawer-close]')) { closeDrawer(); return; }
      if (e.target.matches('[data-cw-drawer-scrim]')) { closeDrawer(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) closeDrawer();
    });
    window.addEventListener('popstate', function () {
      if ((window.location.hash || '').indexOf('cocktail=') === -1 && drawer && drawer.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    // hashchange listener so the curated "edits" cards (#base=Tequila,Mezcal,
    // #producer=Vedrenne, etc.) actually filter the discovery grid when
    // clicked while on the same page. Previously, hash-only navigation just
    // scrolled — filters only applied on a fresh page load with the hash in
    // the URL. Now: clear current filters, re-hydrate from the new hash,
    // repaint, and scroll the discovery section into view.
    window.addEventListener('hashchange', function () {
      var h = (window.location.hash || '').replace(/^#/, '');
      // Drawer-open hashes (#cocktail=...) are handled by the drawer code path;
      // don't reset filters in that case.
      if (h.indexOf('cocktail=') !== -1) return;
      // Clear current filter state
      ['base', 'prod', 'liq', 'ing'].forEach(function (g) {
        if (STATE.filters[g] && STATE.filters[g].clear) STATE.filters[g].clear();
      });
      // Re-hydrate from new hash + repaint
      hydrateFromHash();
      render();
      keepGridInView();
    });
  }

  // ==========================================================================
  // 10. Menu Builder — tick, panel, modal, cart bulk-add
  // ==========================================================================
  var BUILDER = (function () {
    if (!builder) return { wire: function () {}, render: function () {} };

    var panel        = builder.querySelector('[data-cw-builder-panel]');
    var panelHead    = builder.querySelector('[data-cw-builder-toggle]');
    var panelBody    = builder.querySelector('#cw-ch-builder-summary');
    var modal        = builder.querySelector('[data-cw-builder-modal]');
    var modalScrim   = builder.querySelector('[data-cw-builder-modal-scrim]');
    var cocktailsEl  = builder.querySelector('[data-cw-builder-cocktails]');
    var bottlesEl    = builder.querySelector('[data-cw-builder-bottles]');
    var retailEl     = builder.querySelector('[data-cw-builder-retail]');
    var servesEl     = builder.querySelector('[data-cw-builder-serves]');
    var statusEl     = builder.querySelector('[data-cw-builder-status]');

    var cocktailsPerBottle = parseFloat(builder.getAttribute('data-cocktails-per-bottle')) || 20;

    function showHide() {
      var hasItems = STATE.menu.size > 0;
      if (hasItems) builder.removeAttribute('hidden');
      else builder.setAttribute('hidden', '');
    }

    function setPlural(handles) {
      var n = handles.length;
      builder.querySelectorAll('[data-cw-builder-cocktail-count], [data-cw-builder-cocktail-count-2]').forEach(function (el) { el.textContent = n; });
      builder.querySelectorAll('[data-cw-builder-plural], [data-cw-builder-plural-2]').forEach(function (el) { el.textContent = n === 1 ? '' : 's'; });
    }

    // Build the aggregated bottle list.
    function aggregateBottles(handles) {
      // Key by vid (cart needs variant ID, not product handle).
      var map = Object.create(null);
      handles.forEach(function (h) {
        var c = BY_HANDLE[h];
        if (!c) return;
        c.u.forEach(function (p) {
          if (!p.vid) return; // skip products without a variant ID (data drift)
          if (p.v === 'Massenez') return; // decoy policy — don't surface for cart
          if (!map[p.vid]) {
            map[p.vid] = { h: p.h, t: p.t, v: p.v, ty: p.ty, px: p.px, vid: p.vid, usedBy: [] };
          }
          map[p.vid].usedBy.push(c.n);
        });
      });
      return Object.keys(map).map(function (k) { return map[k]; });
    }

    function maths(handles, bottles) {
      var retail = parseFloat((retailEl && retailEl.value) || '0') || 0;
      var serves = parseFloat((servesEl && servesEl.value) || '0') || 0;

      // GST: AU 10% — retail input is GST-inclusive (it's the menu price),
      // convert to ex-GST by dividing by 1.1.
      var retailExGst = retail / 1.1;

      // Per-cocktail pour cost = sum of (px / cocktailsPerBottle) over ingredients.
      // Multi-cocktail menus average their pour costs for the headline figure.
      var perCocktail = handles.map(function (h) {
        var c = BY_HANDLE[h];
        if (!c) return { gp: 0, pourCost: 0 };
        var pourCost = 0;
        c.u.forEach(function (p) {
          if (p.v === 'Massenez') return;
          pourCost += (p.px || 0) / cocktailsPerBottle;
        });
        var gp = retail > 0 ? (retailExGst - pourCost) : 0;
        return { gp: gp, pourCost: pourCost };
      });

      var avgPour = perCocktail.length
        ? perCocktail.reduce(function (s, c) { return s + c.pourCost; }, 0) / perCocktail.length
        : 0;
      var avgGp   = perCocktail.length
        ? perCocktail.reduce(function (s, c) { return s + c.gp; }, 0) / perCocktail.length
        : 0;

      // Weekly GP = sum across cocktails of (gp * serves).
      var weeklyGp = perCocktail.reduce(function (s, c) { return s + c.gp * serves; }, 0);

      var wholesale = bottles.reduce(function (s, b) { return s + (b.px || 0); }, 0);

      return {
        avgPour: avgPour,
        avgGp: avgGp,
        weeklyGp: weeklyGp,
        wholesale: wholesale,
        bottleCount: bottles.length
      };
    }

    function paint() {
      var handles = Array.from(STATE.menu);
      var bottles = aggregateBottles(handles);
      var m = maths(handles, bottles);

      // Plural / count
      setPlural(handles);

      // Panel + foot counters
      builder.querySelectorAll('[data-cw-builder-bottle-count], [data-cw-builder-bottle-count-2], [data-cw-builder-bottle-count-3]').forEach(function (el) { el.textContent = m.bottleCount; });
      builder.querySelectorAll('[data-cw-builder-wholesale], [data-cw-builder-wholesale-2], [data-cw-builder-wholesale-3]').forEach(function (el) { el.textContent = fmt2(m.wholesale); });

      // Maths summary
      var pourEl  = builder.querySelector('[data-cw-builder-pour-cost]');
      var gpEl    = builder.querySelector('[data-cw-builder-gp-per]');
      var wkGpEl  = builder.querySelector('[data-cw-builder-weekly-gp]');
      if (pourEl) pourEl.textContent = fmt2(m.avgPour);
      if (gpEl)   gpEl.textContent   = fmt2(m.avgGp);
      if (wkGpEl) wkGpEl.textContent = fmt2(m.weeklyGp);

      // Cocktails list (modal)
      if (cocktailsEl) {
        cocktailsEl.innerHTML = handles.map(function (h) {
          var c = BY_HANDLE[h];
          if (!c) return '';
          var ingCount = countIngredients(c.i);
          return ''
            + '<li class="cw-ch-builder__cocktail">'
            +   '<div>'
            +     '<div class="cw-ch-builder__cocktail-name">' + escapeHTML(c.n) + '</div>'
            +     '<div class="cw-ch-builder__cocktail-meta">' + ingCount + ' ingredient' + (ingCount === 1 ? '' : 's') + '</div>'
            +   '</div>'
            +   '<button class="cw-ch-builder__cocktail-remove" type="button" data-cw-tick="' + escapeAttr(h) + '" aria-label="Remove ' + escapeAttr(c.n) + ' from menu">'
            +     '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="3" y1="3" x2="11" y2="11"/><line x1="11" y1="3" x2="3" y2="11"/></svg>'
            +   '</button>'
            + '</li>';
        }).join('');
      }

      // Bottles list (modal)
      if (bottlesEl) {
        bottles.sort(function (a, b) {
          if (a.v !== b.v) return (a.v || '').localeCompare(b.v || '');
          return (a.t || '').localeCompare(b.t || '');
        });
        bottlesEl.innerHTML = bottles.map(function (b) {
          var meta = [];
          if (b.v) meta.push(escapeHTML(b.v));
          if (b.ty) meta.push(escapeHTML(b.ty));
          if (b.usedBy.length) meta.push('Used in ' + b.usedBy.length + ' cocktail' + (b.usedBy.length === 1 ? '' : 's'));
          return ''
            + '<li class="cw-ch-builder__bottle">'
            +   '<div>'
            +     '<a class="cw-ch-builder__bottle-title" href="/products/' + escapeAttr(b.h) + '" target="_blank" rel="noopener">' + escapeHTML(b.t) + '</a>'
            +     '<div class="cw-ch-builder__bottle-meta">' + meta.join(' · ') + '</div>'
            +   '</div>'
            +   '<div class="cw-ch-builder__bottle-qty">×1</div>'
            +   '<div class="cw-ch-builder__bottle-price">$' + fmt2(b.px) + ' </div>'
            + '</li>';
        }).join('') || '<li class="cw-ch-builder__bottle"><em>No bottles in menu yet — pick some cocktails.</em></li>';
      }

      showHide();
    }

    function togglePanelBody() {
      if (!panelHead || !panelBody) return;
      var isOpen = panelHead.getAttribute('aria-expanded') === 'true';
      panelHead.setAttribute('aria-expanded', String(!isOpen));
      if (isOpen) panelBody.setAttribute('hidden', '');
      else panelBody.removeAttribute('hidden');
    }

    function openModal() {
      if (!modal) return;
      paint();
      modal.classList.add('is-open');
      modal.removeAttribute('aria-hidden');
      document.body.classList.add('cw-ch-builder-modal-open');
    }
    function closeModal() {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cw-ch-builder-modal-open');
    }

    function clearAll() {
      STATE.menu.clear();
      saveMenuToStorage();
      paint();
      repaintCardTickStates();
      // Update drawer button if open
      var dBtn = drawer && drawer.querySelector('[data-cw-tick]');
      if (dBtn) {
        dBtn.classList.remove('is-ticked');
        dBtn.textContent = 'Save for later';
      }
    }

    function addAllToCart() {
      var bottles = aggregateBottles(Array.from(STATE.menu));
      if (!bottles.length) return;
      var items = bottles
        .filter(function (b) { return b.vid; })
        .map(function (b) { return { id: b.vid, quantity: 1 }; });
      if (!items.length) {
        setStatus('No variants available', true);
        return;
      }
      setStatus('Adding…', false);
      var btn = builder.querySelector('[data-cw-builder-add-to-cart]');
      if (btn) btn.setAttribute('disabled', '');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      }).then(function () {
        setStatus(items.length + ' bottles added ✓', false);
        // Bump the cart icon if the theme emits an event
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        setTimeout(function () { setStatus('', true); }, 4000);
      }).catch(function (err) {
        console.error('[cw-cocktail-hub] cart add failed', err);
        setStatus('Cart add failed — see console', false);
      }).finally(function () {
        if (btn) btn.removeAttribute('disabled');
      });
    }

    function setStatus(text, hide) {
      if (!statusEl) return;
      if (!text || hide) {
        statusEl.textContent = '';
        statusEl.setAttribute('hidden', '');
      } else {
        statusEl.textContent = text;
        statusEl.removeAttribute('hidden');
      }
    }

    function printMenu() {
      // Modal must be open for print stylesheet to capture it.
      openModal();
      setTimeout(function () { window.print(); }, 50);
    }

    function wire() {
      // Tick (delegated across grid + drawer + modal cocktail list)
      document.addEventListener('click', function (e) {
        var t = e.target.closest('[data-cw-tick]');
        if (!t) return;
        e.preventDefault();
        e.stopPropagation();
        var h = t.getAttribute('data-cw-tick');
        if (!h) return;
        if (STATE.menu.has(h)) STATE.menu.delete(h);
        else STATE.menu.add(h);
        saveMenuToStorage();
        paint();
        repaintCardTickStates();

        // Update drawer add-to button label if it's for the same handle
        if (drawer && drawer.classList.contains('is-open')) {
          var drawerBtn = drawer.querySelector('[data-cw-tick="' + h.replace(/"/g, '\\"') + '"]');
          if (drawerBtn) {
            var isTicked = STATE.menu.has(h);
            drawerBtn.classList.toggle('is-ticked', isTicked);
            drawerBtn.textContent = isTicked ? 'Saved — remove' : 'Save for later';
          }
        }
      });

      // Panel toggle (collapse/expand body)
      if (panelHead) panelHead.addEventListener('click', togglePanelBody);

      // Open modal
      var openModalBtn = builder.querySelector('[data-cw-builder-open-modal]');
      if (openModalBtn) openModalBtn.addEventListener('click', openModal);

      // Close modal
      var closeBtn = builder.querySelector('[data-cw-builder-modal-close]');
      if (closeBtn) closeBtn.addEventListener('click', closeModal);
      if (modalScrim) modalScrim.addEventListener('click', closeModal);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) closeModal();
      });

      // Clear
      var clearBtn = builder.querySelector('[data-cw-builder-clear]');
      if (clearBtn) clearBtn.addEventListener('click', clearAll);

      // Maths inputs (live recalc)
      [retailEl, servesEl].forEach(function (el) { if (el) el.addEventListener('input', paint); });

      // Cart add
      var addBtn = builder.querySelector('[data-cw-builder-add-to-cart]');
      if (addBtn) addBtn.addEventListener('click', addAllToCart);

      // Print
      var printBtn = builder.querySelector('[data-cw-builder-print]');
      if (printBtn) printBtn.addEventListener('click', printMenu);
    }

    return { wire: wire, render: paint };
  })();

  // ==========================================================================
  // 10b. Storefront API fetch — pulls cocktails beyond Liquid's 50-cap.
  // ==========================================================================
  // Shopify Liquid `shop.metaobjects.<type>.values` is hard-capped at 50 and
  // doesn't accept paginate. To show the full 500+ cocktail catalogue we fetch
  // the rest via the Storefront API on the client. Public access token is
  // exposed via window.CW_STOREFRONT (safe — scoped to read metaobjects).
  //
  // After fetch: merge into ALL (dedupe by handle), rebuild BY_HANDLE and
  // BOTTLE_INDEX, re-run render() + BUILDER.render() + REVERSE.render().

  // Same eligibility rules as the Liquid data-island:
  //   1. at least ONE product in products_used has a "spirits family"
  //      product_type OR a spirit:* tag (excluding spirit:wine).
  //   2. NOT 100% Massenez (decoy policy).
  var SPIRIT_TYPE_KEYWORDS = ('spirits,liqueur,cognac,calvados,armagnac,rum,'
    + 'gin,whisky,whiskey,bourbon,tequila,mezcal,eau de vie,fruit liqueur,'
    + 'fruit in spirit,fruit in spirits,fruit spirits,fruit cremes,absinthe,'
    + 'absynthe,pastis,marc,brandy,cachaca,pisco,schnapps,vermouth,bitter,'
    + 'bitters,amaro,vodka,sambuca,grappa,mistelles,lemoncello,italian liqueurs,'
    + 'other liqueurs,australian spirit,spirit italian,aperitif,aperitifs france,'
    + 'aperitifs other countries,aperitvo/digestivo italy,bourbon /american whisky'
  ).split(',');

  function isSpiritEligible(cocktail) {
    if (!cocktail || !cocktail.u || !cocktail.u.length) return false;
    var hasSpirit = false;
    var nonMassenezCount = 0;
    for (var i = 0; i < cocktail.u.length; i++) {
      var p = cocktail.u[i];
      if (!p) continue;
      if (!hasSpirit) {
        var tyLower = (p.ty || '').toLowerCase();
        for (var k = 0; k < SPIRIT_TYPE_KEYWORDS.length; k++) {
          var kw = SPIRIT_TYPE_KEYWORDS[k];
          if (kw && tyLower.indexOf(kw) !== -1) { hasSpirit = true; break; }
        }
      }
      if (!hasSpirit && p.tg) {
        var tags = String(p.tg).split('|');
        for (var t = 0; t < tags.length; t++) {
          if (tags[t].indexOf('spirit:') === 0 && tags[t] !== 'spirit:wine') {
            hasSpirit = true; break;
          }
        }
      }
      if (p.v !== 'Massenez') nonMassenezCount++;
    }
    return hasSpirit && nonMassenezCount > 0;
  }

  // Convert a Storefront-API metaobject node → data-island object shape.
  function storefrontNodeToCocktail(node) {
    var c = { h: node.handle, n: node.handle, o: '', i: '', m: '', p: null, alt: null, u: [] };
    var fields = node.fields || [];
    for (var f = 0; f < fields.length; f++) {
      var field = fields[f];
      if (!field || !field.key) continue;
      switch (field.key) {
        case 'name':
          if (field.value) c.n = field.value;
          break;
        case 'origin':
          if (field.value) c.o = field.value;
          break;
        case 'ingredients':
          if (field.value) c.i = field.value;
          break;
        case 'method':
          if (field.value) c.m = field.value;
          break;
        case 'hero_photo':
          if (field.reference && field.reference.image) {
            c.p = field.reference.image.url || null;
            c.alt = field.reference.image.altText || c.n || null;
          }
          break;
        case 'products_used':
          if (field.references && field.references.nodes) {
            c.u = field.references.nodes.map(function (p) {
              if (!p) return null;
              var variant = p.variants && p.variants.nodes && p.variants.nodes[0];
              var rawVid = variant ? (variant.id || '') : '';
              var vidMatch = String(rawVid).match(/(\d+)$/);
              var px = variant && variant.price ? parseFloat(variant.price.amount) : 0;
              // Apply the same wine ×1.29 (embedded WET) logic as Liquid.
              var typ = p.productType || '';
              var tagsArr = p.tags || [];
              var tagsJoined = tagsArr.join('|');
              if (/Wine/.test(typ) || tagsJoined.indexOf('spirit:wine') !== -1) {
                px = px * 1.29;
              }
              return {
                h: p.handle,
                t: p.title || p.handle,
                v: p.vendor || '',
                ty: typ,
                tg: tagsJoined,
                px: Math.round(px * 100) / 100,
                vid: vidMatch ? parseInt(vidMatch[1], 10) : 0
              };
            }).filter(Boolean);
          }
          break;
      }
    }
    if (typeof c.n !== 'string') c.n = c.h;
    if (typeof c.alt !== 'string') c.alt = c.n;
    return c;
  }

  function fetchAllCocktailsFromStorefront() {
    if (!window.CW_STOREFRONT || !window.CW_STOREFRONT.token) {
      return Promise.resolve([]);
    }
    var cfg = window.CW_STOREFRONT;
    var endpoint = '/api/' + (cfg.apiVersion || '2024-10') + '/graphql.json';
    var query = ''
      + 'query GetCocktails($cursor: String) {'
      + '  metaobjects(type: "cocktail", first: 250, after: $cursor) {'
      + '    nodes {'
      + '      handle'
      + '      fields {'
      + '        key value type'
      + '        reference {'
      + '          ... on MediaImage { image { url altText } }'
      + '        }'
      + '        references(first: 25) {'
      + '          nodes {'
      + '            ... on Product {'
      + '              handle title vendor productType tags'
      + '              variants(first: 1) { nodes { id price { amount } } }'
      + '            }'
      + '          }'
      + '        }'
      + '      }'
      + '    }'
      + '    pageInfo { hasNextPage endCursor }'
      + '  }'
      + '}';

    var collected = [];
    function fetchPage(cursor) {
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Shopify-Storefront-Access-Token': cfg.token
        },
        body: JSON.stringify({ query: query, variables: { cursor: cursor } })
      })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (j.errors) {
            console.error('[cw-cocktail-hub] Storefront errors', j.errors);
            return collected;
          }
          var page = j && j.data && j.data.metaobjects;
          if (!page) return collected;
          (page.nodes || []).forEach(function (n) { collected.push(n); });
          if (page.pageInfo && page.pageInfo.hasNextPage && collected.length < 2000) {
            return fetchPage(page.pageInfo.endCursor);
          }
          return collected;
        })
        .catch(function (err) {
          console.error('[cw-cocktail-hub] Storefront fetch failed', err);
          return collected;
        });
    }

    return fetchPage(null);
  }

  // Rebuild the dynamic filter chip groups (Liqueur Family + Producer) from the
  // full in-memory cocktail catalog. Server-side Liquid only sees the first 50
  // cocktails (hard cap), so chips for flavours / producers that only appear in
  // cocktails 51+ would be missing without this. Called after Storefront fetch.
  function humanLabel(slug) {
    if (!slug) return '';
    return String(slug).split('-').map(function (w) {
      return w ? (w.charAt(0).toUpperCase() + w.slice(1)) : '';
    }).join(' ');
  }

  function rebuildFilterChips() {
    if (!filterRail) return;

    // Collect unique flavour:* tag values across every product in every cocktail.
    var flavourSet = Object.create(null);
    var vendorSet  = Object.create(null);
    for (var i = 0; i < ALL.length; i++) {
      var c = ALL[i];
      if (!c || !c.u) continue;
      for (var j = 0; j < c.u.length; j++) {
        var p = c.u[j];
        if (!p) continue;
        if (p.v && p.v !== 'Massenez') vendorSet[p.v] = 1;
        if (!p.tg) continue;
        var tags = String(p.tg).split('|');
        for (var t = 0; t < tags.length; t++) {
          var tag = tags[t];
          if (tag.indexOf('flavour:') === 0) {
            var fv = tag.slice('flavour:'.length).trim();
            if (fv) flavourSet[fv] = 1;
          }
        }
      }
    }

    function rebuildGroup(filterKey, valuesSet) {
      var sample = filterRail.querySelector('[data-cw-filter="' + filterKey + '"]');
      if (!sample) return;
      var body = sample.parentNode;
      var values = Object.keys(valuesSet).sort(function (a, b) {
        return a.toLowerCase().localeCompare(b.toLowerCase());
      });
      // Preserve active state per value.
      var activeSet = STATE.filters[filterKey === 'prod' ? 'prod' : (filterKey === 'liq' ? 'liq' : filterKey)];
      body.innerHTML = values.map(function (v) {
        var label = humanLabel(v);
        var isActive = activeSet && activeSet.has(v);
        return '<button class="cw-ch-rail-chip' + (isActive ? ' is-active' : '') + '" '
             + 'type="button" data-cw-filter="' + filterKey + '" '
             + 'data-cw-value="' + escapeAttr(v) + '">' + escapeHTML(label) + '</button>';
      }).join('');
    }

    rebuildGroup('liq', flavourSet);
    rebuildGroup('prod', vendorSet);
  }

  // Merge fetched cocktails into ALL (in place), then re-derive everything
  // downstream. Idempotent — re-runnable.
  function mergeStorefrontCocktails(rawNodes) {
    if (!rawNodes || !rawNodes.length) return 0;
    var existingHandles = Object.create(null);
    for (var i = 0; i < ALL.length; i++) existingHandles[ALL[i].h] = 1;

    var added = 0;
    for (var j = 0; j < rawNodes.length; j++) {
      var node = rawNodes[j];
      if (!node || !node.handle) continue;
      if (existingHandles[node.handle]) continue; // already in initial data island
      var c = storefrontNodeToCocktail(node);
      if (!isSpiritEligible(c)) continue;          // apply same filter
      // Normalisation (mirror what the boot block does on the inline ALL).
      if (typeof c.n !== 'string') c.n = c.h || '';
      if (typeof c.o !== 'string') c.o = '';
      if (typeof c.i !== 'string') c.i = '';
      if (typeof c.m !== 'string') c.m = '';
      if (typeof c.alt !== 'string') c.alt = c.n || '';
      if (typeof c.p !== 'string' && c.p !== null) c.p = null;
      ALL.push(c);
      BY_HANDLE[c.h] = c;
      // Update BOTTLE_INDEX (used by reverse-lookup picker) with this cocktail's products
      for (var u = 0; u < c.u.length; u++) {
        var p = c.u[u];
        if (!p || !p.h || p.v === 'Massenez') continue;
        var entry = BOTTLE_INDEX[p.h];
        if (!entry) {
          entry = BOTTLE_INDEX[p.h] = {
            h: p.h, t: p.t || p.h, v: p.v || '', ty: p.ty || '',
            vid: p.vid || 0, px: p.px || 0,
            cocktailHandles: []
          };
        }
        if (entry.cocktailHandles.indexOf(c.h) === -1) entry.cocktailHandles.push(c.h);
      }
      added++;
    }
    return added;
  }

  // ==========================================================================
  // 11. Reverse Lookup — "stock → cocktails" up-sell ratchet
  // ==========================================================================
  var REVERSE = (function () {
    var section      = document.querySelector('[data-cw-hub-reverse]');
    if (!section) return { wire: function () {}, render: function () {} };

    var searchEl     = section.querySelector('[data-cw-reverse-search]');
    var bottleListEl = section.querySelector('[data-cw-reverse-bottles]');
    var pourCountEl  = section.querySelector('[data-cw-reverse-pour-count]');
    var pourPluralEl = section.querySelector('[data-cw-reverse-pour-plural]');
    var clearBtn     = section.querySelector('[data-cw-reverse-clear]');
    var statsEl      = section.querySelector('[data-cw-reverse-stats]');
    var readyCountEl = section.querySelector('[data-cw-reverse-ready-count]');
    var oneAwayEl    = section.querySelector('[data-cw-reverse-oneaway-count]');
    var twoAwayEl    = section.querySelector('[data-cw-reverse-twoaway-count]');
    var emptyEl      = section.querySelector('[data-cw-reverse-empty]');
    var unlockEl     = section.querySelector('[data-cw-reverse-unlock]');
    var unlockListEl = section.querySelector('[data-cw-reverse-unlock-list]');
    var cocktailsSec = section.querySelector('[data-cw-reverse-cocktails-section]');
    var cocktailsEl  = section.querySelector('[data-cw-reverse-cocktail-list]');

    var searchQuery = '';

    function bottleSortKey(b) {
      // Sort: ticked first, then by descending cocktail-count, then vendor, then title
      var tickedRank = STATE.pours.has(b.h) ? 0 : 1;
      var cocktailCount = b.cocktailHandles.length;
      return [tickedRank, -cocktailCount, (b.v || '').toLowerCase(), (b.t || '').toLowerCase()];
    }

    function compareArr(a, b) {
      for (var i = 0; i < a.length && i < b.length; i++) {
        if (a[i] < b[i]) return -1;
        if (a[i] > b[i]) return 1;
      }
      return 0;
    }

    function renderBottleList() {
      if (!bottleListEl) return;
      var all = Object.keys(BOTTLE_INDEX).map(function (k) { return BOTTLE_INDEX[k]; });
      var q = (searchQuery || '').toLowerCase().trim();
      var filtered = q
        ? all.filter(function (b) {
            return (b.t || '').toLowerCase().indexOf(q) !== -1
                || (b.v || '').toLowerCase().indexOf(q) !== -1
                || (b.ty || '').toLowerCase().indexOf(q) !== -1;
          })
        : all;
      filtered.sort(function (a, b) { return compareArr(bottleSortKey(a), bottleSortKey(b)); });

      bottleListEl.innerHTML = filtered.map(function (b) {
        var ticked = STATE.pours.has(b.h);
        var meta = [];
        if (b.v) meta.push(escapeHTML(b.v));
        if (b.ty) meta.push(escapeHTML(b.ty));
        return ''
          + '<button class="cw-ch-reverse-bottle' + (ticked ? ' is-ticked' : '') + '" '
          +   'type="button" data-cw-pour="' + escapeAttr(b.h) + '" '
          +   'aria-pressed="' + (ticked ? 'true' : 'false') + '">'
          +   '<span class="cw-ch-reverse-bottle__check" aria-hidden="true">'
          +     (ticked
              ? '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2.4"><polyline points="2,6 5,9 10,3"/></svg>'
              : '')
          +   '</span>'
          +   '<span class="cw-ch-reverse-bottle__body">'
          +     '<span class="cw-ch-reverse-bottle__title">' + escapeHTML(b.t) + '</span>'
          +     '<span class="cw-ch-reverse-bottle__meta">' + meta.join(' · ') + '</span>'
          +   '</span>'
          +   '<span class="cw-ch-reverse-bottle__count" aria-label="' + b.cocktailHandles.length + ' cocktails use this">'
          +     b.cocktailHandles.length
          +   '</span>'
          + '</button>';
      }).join('');
    }

    // For each cocktail, compute how many of its products are missing from
    // STATE.pours, and which product is the single missing one (if exactly 1).
    function computeCocktailStatuses() {
      var ready = [];   // 0-missing
      var oneAway = []; // 1-missing
      var twoAway = []; // 2-missing
      for (var i = 0; i < ALL.length; i++) {
        var c = ALL[i];
        var missing = [];
        for (var j = 0; j < c.u.length; j++) {
          var p = c.u[j];
          if (!p || !p.h) continue;
          if (p.v === 'Massenez') continue; // decoy policy
          if (!STATE.pours.has(p.h)) missing.push(p);
        }
        if (missing.length === 0 && c.u.length > 0) ready.push({ c: c, missing: missing });
        else if (missing.length === 1) oneAway.push({ c: c, missing: missing });
        else if (missing.length === 2) twoAway.push({ c: c, missing: missing });
      }
      return { ready: ready, oneAway: oneAway, twoAway: twoAway };
    }

    // For each bottle NOT in pours, count how many one-away cocktails it would
    // unlock if added. Rank top 6.
    function computeUnlockCandidates(oneAway) {
      var liftByHandle = Object.create(null);
      var sampleByHandle = Object.create(null);
      for (var i = 0; i < oneAway.length; i++) {
        var entry = oneAway[i];
        var missingP = entry.missing[0];
        if (!missingP || !missingP.h) continue;
        if (!liftByHandle[missingP.h]) {
          liftByHandle[missingP.h] = { bottle: missingP, lift: 0, unlocks: [] };
        }
        liftByHandle[missingP.h].lift += 1;
        if (liftByHandle[missingP.h].unlocks.length < 3) {
          liftByHandle[missingP.h].unlocks.push(entry.c.n);
        }
      }
      var ranked = Object.keys(liftByHandle).map(function (k) { return liftByHandle[k]; });
      ranked.sort(function (a, b) { return b.lift - a.lift; });
      return ranked.slice(0, 6);
    }

    function renderUnlockPanel(unlockCandidates) {
      if (!unlockEl || !unlockListEl) return;
      if (!unlockCandidates.length) {
        unlockEl.setAttribute('hidden', '');
        return;
      }
      unlockEl.removeAttribute('hidden');
      unlockListEl.innerHTML = unlockCandidates.map(function (cand) {
        var b = cand.bottle;
        // Pull the rich BOTTLE_INDEX record (has vid + cocktail count for context).
        var rich = BOTTLE_INDEX[b.h] || b;
        var meta = [];
        if (rich.v) meta.push(escapeHTML(rich.v));
        if (rich.ty) meta.push(escapeHTML(rich.ty));
        var unlockSample = cand.unlocks.length
          ? '<p class="cw-ch-reverse-unlock-card__unlocks">Unlocks: ' + cand.unlocks.map(escapeHTML).join(' · ') + (cand.lift > cand.unlocks.length ? ' (+' + (cand.lift - cand.unlocks.length) + ' more)' : '') + '</p>'
          : '';
        return ''
          + '<li class="cw-ch-reverse-unlock-card">'
          +   '<h4 class="cw-ch-reverse-unlock-card__title">' + escapeHTML(rich.t) + '</h4>'
          +   '<p class="cw-ch-reverse-unlock-card__meta">' + meta.join(' · ') + '</p>'
          +   '<p class="cw-ch-reverse-unlock-card__lift">'
          +     '<strong>+' + cand.lift + '</strong> cocktail' + (cand.lift === 1 ? '' : 's') + ' unlocked'
          +   '</p>'
          +   '<p class="cw-ch-reverse-unlock-card__price">$' + fmt2(rich.px) + ' </p>'
          +   unlockSample
          +   '<div class="cw-ch-reverse-unlock-card__ctas">'
          +     '<button type="button" class="cw-ch-reverse-unlock-card__btn" data-cw-pour="' + escapeAttr(rich.h) + '">'
          +       'Mark as stocked'
          +     '</button>'
          +     '<a class="cw-ch-reverse-unlock-card__btn cw-ch-reverse-unlock-card__btn--primary" href="/products/' + escapeAttr(rich.h) + '" target="_blank" rel="noopener">'
          +       'View bottle &rarr;'
          +     '</a>'
          +   '</div>'
          + '</li>';
      }).join('');
    }

    function renderCocktailList(statuses) {
      if (!cocktailsSec || !cocktailsEl) return;
      var combined = statuses.ready.map(function (e) { return { e: e, mode: 'ready' }; })
        .concat(statuses.oneAway.map(function (e) { return { e: e, mode: 'oneaway' }; }));
      if (!combined.length) {
        cocktailsSec.setAttribute('hidden', '');
        return;
      }
      cocktailsSec.removeAttribute('hidden');

      cocktailsEl.innerHTML = combined.map(function (item) {
        var c = item.e.c;
        var photo = c.p
          ? '<div class="cw-ch-reverse-cocktail__photo"><img src="' + escapeAttr(c.p) + '" alt="' + escapeAttr(c.alt || c.n) + '" loading="lazy"></div>'
          : '<div class="cw-ch-reverse-cocktail__photo" aria-hidden="true"></div>';
        var status, missingLine;
        if (item.mode === 'ready') {
          status = '<span class="cw-ch-reverse-cocktail__status cw-ch-reverse-cocktail__status--ready">Ready to pour</span>';
          missingLine = '';
        } else {
          var missingTitle = (item.e.missing[0] && item.e.missing[0].t) || 'one bottle';
          status = '<span class="cw-ch-reverse-cocktail__status cw-ch-reverse-cocktail__status--oneaway">1 bottle away</span>';
          missingLine = '<span class="cw-ch-reverse-cocktail__missing">Need: ' + escapeHTML(missingTitle) + '</span>';
        }
        return ''
          + '<li>'
          +   '<button class="cw-ch-reverse-cocktail" type="button" data-cw-open="' + escapeAttr(c.h) + '">'
          +     photo
          +     '<div class="cw-ch-reverse-cocktail__body">'
          +       '<h4 class="cw-ch-reverse-cocktail__name">' + escapeHTML(c.n) + '</h4>'
          +       status
          +       missingLine
          +     '</div>'
          +   '</button>'
          + '</li>';
      }).join('');
    }

    function paint() {
      var pourCount = STATE.pours.size;
      if (pourCountEl) pourCountEl.textContent = pourCount;
      if (pourPluralEl) pourPluralEl.textContent = pourCount === 1 ? '' : 's';

      var statuses = computeCocktailStatuses();
      if (readyCountEl) readyCountEl.textContent = pourCount === 0 ? '—' : statuses.ready.length;
      if (oneAwayEl)    oneAwayEl.textContent    = pourCount === 0 ? '—' : statuses.oneAway.length;
      if (twoAwayEl)    twoAwayEl.textContent    = pourCount === 0 ? '—' : statuses.twoAway.length;

      // Show empty state only when nothing is picked yet
      if (emptyEl) emptyEl.style.display = pourCount === 0 ? '' : 'none';

      var unlockCandidates = pourCount === 0 ? [] : computeUnlockCandidates(statuses.oneAway);
      renderUnlockPanel(unlockCandidates);
      renderCocktailList(statuses);

      // Repaint bottle list to update ticked / sort order
      renderBottleList();
    }

    function togglePour(handle) {
      if (!handle) return;
      if (STATE.pours.has(handle)) STATE.pours.delete(handle);
      else STATE.pours.add(handle);
      savePoursToStorage();
      paint();
    }

    function clearAll() {
      STATE.pours.clear();
      savePoursToStorage();
      paint();
    }

    function wire() {
      // Pour-toggle click (delegated — bottle picker AND unlock-card "Mark as stocked")
      section.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-cw-pour]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        togglePour(btn.getAttribute('data-cw-pour'));
      });

      // Search input
      if (searchEl) {
        searchEl.addEventListener('input', function () {
          searchQuery = searchEl.value || '';
          renderBottleList();
        });
      }

      // Clear button
      if (clearBtn) clearBtn.addEventListener('click', clearAll);

      // Cocktail click — open drawer (handled by global drawer wiring via data-cw-open)
    }

    return { wire: wire, render: paint };
  })();

  // ==========================================================================
  // 12. Boot
  // ==========================================================================
  loadMenuFromStorage();
  loadPoursFromStorage();
  var initialDrawerHandle = hydrateFromHash();
  wireFilters();
  wireActiveChips();
  wireSort();
  wireGridDelegate();
  wireDrawer();
  BUILDER.wire();
  REVERSE.wire();
  render();
  BUILDER.render();
  REVERSE.render();
  if (initialDrawerHandle) openDrawer(initialDrawerHandle);

  // Async hydration: fetch the rest of the cocktail catalogue via Storefront
  // API (the inline data island only carries the first 50 due to Liquid's
  // hard cap). When the fetch completes, merge + re-render everywhere.
  fetchAllCocktailsFromStorefront().then(function (nodes) {
    var added = mergeStorefrontCocktails(nodes);
    // Even if no new cocktails were added (catalog ≤ 50), the server-rendered
    // chip rail may still be incomplete (producer / flavour tags from the
    // bottom 90% of cocktails missing). Rebuild chips against the in-memory
    // ALL — covers both cases safely.
    rebuildFilterChips();
    if (!added) {
      render();  // re-render once with refreshed chips
      return;
    }
    console.info('[cw-cocktail-hub] +' + added + ' cocktails from Storefront API (total ' + ALL.length + ')');
    render();
    BUILDER.render();
    REVERSE.render();
  });
})();
